<template>
  <b-row class="my-3">
    <b-col cols="12" sm="6">
      <h4><b>Upload Participants</b></h4>
      <p>Import participants as an Excel file.</p>
      <p>The table must contain the following columns:</p>
      <ul>
        <li>First column: First Name</li>
        <li>Second column: Last Name</li>
        <li>Third column: Email Address</li>
        <li>Fourth optional column: Tracking Code (optional)</li>
      </ul>
      <p>Please make sure that the uploaded file has a <b>.xlsx file extension</b> and that it does <b>not</b> have any header rows.</p>
      <b-form-file
        placeholder="Upload an Excel spreadsheet in xlsx format…"
        drop-placeholder="Drop an Excel spreadsheet in xlsx format…"
        accept=".xlsx"
        @change="importFile"
      ></b-form-file>
      <hr />
      <b-alert class="mb-3" show :variant="total ? 'outline-primary' : 'outline-secondary'">
        <strong>{{ total | plural('participant') }} are in selection</strong>
      </b-alert>
    </b-col>
    <b-col cols="12" sm="6" class="border-left">
      <div>
        <b-input v-model="searchTerm" placeholder="Search Employee..." />
      </div>
      <hr class="d-sm-none"/>
      <b-list-group v-if="Array.isArray(filteredParticipants) && filteredParticipants.length" class="my-3">
        <b-list-group-item v-for="entry in filteredParticipants" :key="entry.emailAddress" id="participants-list">
          <div class="d-flex justify-content-between">
            <span>{{ entry.firstName }} {{ entry.lastName }} ({{ entry.emailAddress }})</span>
            <span v-if="entry.location" style="display: inline-block; max-width: 70px" class="text-muted text-truncate">{{ entry.location }}</span>
            <span v-if="entry.department" style="display: inline-block; max-width: 70px" class="text-muted text-truncate">{{ entry.department }}</span>
            <span v-if="entry.trackingCode" style="display: inline-block; max-width: 120px" class="text-muted text-truncate">{{ entry.trackingCode }}</span>
          </div>
        </b-list-group-item>
      </b-list-group>

      <div v-else class="p-4 text-center">No Employees Found</div>

      <!-- Pagination -->
      <div class="d-flex justify-content-center">
        <b-pagination v-model="page" :total-rows="total" :per-page="limit" aria-controls="participants-list" />
      </div>
    </b-col>
    <b-col class="d-flex flex-row justify-content-start align-items-center" cols="12">
      <b-btn @click="back" variant="transparent">Back</b-btn>
      <div class="flex-fill"/>
      <b-btn class="mr-2" @click="saveParticipants" variant="outline-primary">Save</b-btn>
    </b-col>
  </b-row>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import Excel from 'exceljs';

export default {
  name: 'CampaignAdminScimUsersSegment',
  props: {
    campaign: {
      type: Object,
    },
    value: {
      type: Array,
    }
  },
  data: () => ({
    searchTerm: null,
    page: 1,
    limit: 10,
    participants: null,
    initialSelectedParticipants: null,
  }),
  created() {
    // Set participants
    const { value } = this;
    if (!Array.isArray(value) || !value.length) return;
    this.participants = value.map(([ firstName, lastName, emailAddress, trackingCode, location, department ]) =>
      ({ firstName, lastName, emailAddress, trackingCode, location, department }));
    this.initialSelectedParticipants = JSON.parse(JSON.stringify(this.participants));
  },
  computed: {
    unsavedChanges() {
      return !_isEqual(this.initialSelectedParticipants, this.participants);
    },
    filteredParticipants() {
      const { participants, searchTerm, page, limit } = this;
      if (!Array.isArray(participants) || !participants.length) return null;
      if (!searchTerm) return participants;
      const search = searchTerm.toLowerCase();
      return participants
        .filter(({ firstName, lastName, emailAddress }) =>
          `${firstName} ${lastName} ${emailAddress}`.toLowerCase().includes(search))
        .slice((page - 1) * limit, page * limit);
    },
    totalInSelection() {
      const { filteredParticipants } = this;
      return Array.isArray(filteredParticipants) ? filteredParticipants.length : 0;
    },
    total() {
      const { participants } = this;
      return Array.isArray(participants) ? participants.length : 0;
    },
  },
  methods: {
    importFile(event) {
      const workbook = new Excel.Workbook();
      const reader = new FileReader();
      const file = event.target.files[0];
      const fileExtension = file.name.split('.').pop();

      const participants = [];
      reader.readAsArrayBuffer(file);
      reader.onload = async () => {
        try {
          const buffer = reader.result;
          this.$assert(
            workbook[fileExtension] && typeof workbook[fileExtension].load === 'function',
            new Error('File type is not allowed.')
          );
          const wb = await workbook[fileExtension].load(buffer);

          const parseCellValue = cell => {
            if (!cell.value) {
              throw new Error(`Error parsing Excel file: the cell in row ${cell.row}, column ${cell.col} is unexpectedly empty.`);
            }

            switch (typeof cell.value) {
              case 'string':
                return cell.value.trim();
              case 'object':
                // if cellValue has a hyperlink property, strip any present mailto: from the value, trim it and return
                if (cell.value.hyperlink && typeof cell.value.hyperlink === 'string') {
                  return cell.value.hyperlink.replace('mailto:', '').trim();
                } else {
                  throw new Error(`Error parsing Excel file: the cell in row ${cell.row}, column ${cell.col} is an unknown object type.`);
                }
              default:
                throw new Error(`Error parsing Excel file: the cell in row ${cell.row}, column ${cell.col} is an unexpected value type.`);
            }
          };

          wb.eachSheet(sheet => {
            console.info({ count: sheet.columnCount, sheet });
            if (sheet.columnCount >= 3) {
              sheet.eachRow((row, rowNumber) => {
                participants.push({});
                const index = participants.length - 1;
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                  switch (colNumber) {
                    case 1:
                      participants[index].firstName = parseCellValue(cell);
                      break;
                    case 2:
                      participants[index].lastName = parseCellValue(cell);
                      break;
                    case 3:
                      const emailAddress = parseCellValue(cell).toLowerCase();

                      if (!emailAddress.includes('@')) {
                        throw new Error(`Error parsing Excel file: the email address ${emailAddress} in row ${cell.row}, column ${cell.col} is not valid.`);
                      }

                      if (participants.find(p => p.emailAddress === emailAddress)) {
                        throw new Error(`Error parsing Excel file: the email address ${emailAddress} in row ${cell.row}, column ${cell.col} is not unique.`);
                      }
                      participants[index].emailAddress = parseCellValue(cell);
                      break;
                    case 4:
                      const trackingCode = cell.value && typeof cell.value === 'string' ? parseCellValue(cell) : undefined;
                      participants[index].trackingCode = trackingCode && trackingCode.length > 0 ? trackingCode : undefined;
                      break;
                    case 5:
                      const location = cell.value && typeof cell.value === 'string' ? parseCellValue(cell) : undefined;
                      participants[index].location = location && location.length > 0 ? location : undefined;
                      break;
                    case 6:
                      const department = cell.value && typeof cell.value === 'string' ? parseCellValue(cell) : undefined;
                      participants[index].department = department && department.length > 0 ? department : undefined;
                      break;
                    default:
                      console.warn('Ignoring extra column in the Excel file.');
                  }
                });
              });
            } else {
              throw new Error('Error parsing Excel file: the number of columns should be between 3 and 6.');
            }
          });
          this.participants = participants;
        } catch (error) {
          this.$log.error(error);
          this.$notifyErr(error);
        }
      };
    },
    saveParticipants() {
      const output = this.participants.map(({ firstName, lastName, emailAddress, trackingCode, location, department }) =>
        [ firstName, lastName, emailAddress, trackingCode, location, department ]);
      this.initialSelectedParticipants = JSON.parse(JSON.stringify(this.participants));
      this.$emit('saveParticipants', output);
    },
    back() {
      this.$emit('back');
    },
  },
};
</script>
<style lang="scss" scoped>
  .inactiveButton {
    color: #001DC0;
    text-decoration: underline;
  }
  .activeButton {
    color: white;
    background-color: #001DC0;
  }
  .activeButton:hover {
    background-color: red;
    color: white;
  }
  .inactiveButton:hover {
    color: white;
    background-color: #001DC0;
  }
  .hidden {
    visibility: hidden;
  }
  .link-styling {
    text-decoration: underline;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-imported-participants.vue